import React, { useState } from 'react';
import loginImage from '../assets/Alliedlogo.png';
import { signUp, logIn, resetPassword } from '../firebase/auth';
import { getAuth } from 'firebase/auth';
import { query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.js';
import '../styling/login.css';

const Login = ({ handleLogin, handleSignup, setStep }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    practice: '',
    role: '',
    password: '',
    confirmPassword: ''
  });

  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResetMessage('');

    try {
      const message = await resetPassword(resetEmail);
      setResetMessage(message);
      setShowResetPassword(false);
    } catch (error) {
      setError(error.message || "An error occurred while resetting the password");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const requiredFields = [
      'firstName', 'lastName', 'mobile', 'practice', 'role', 'email', 'password', 'confirmPassword'
    ];
    
    for (let field of requiredFields) {
      if (!userInfo[field]) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        console.log("Attempting login...");
        const result = await logIn(userInfo.email, userInfo.password);
        console.log("Login result:", result);
        handleLogin();
      
        const userQuery = query(collection(db, 'users'), where('email', '==', result.user.email.toLocaleLowerCase()));
        const practicesQuery = query(collection(db, 'practices'), where('email', '==', result.user.email.toLocaleLowerCase()));
      
        const [userQuerySnapshot, practicesQuerySnapshot] = await Promise.all([
            getDocs(userQuery),
            getDocs(practicesQuery)
        ]);
      
        let userDoc = null;
      
        if (!userQuerySnapshot.empty) {
            userDoc = userQuerySnapshot.docs[0];
        }
        if (!userDoc && !practicesQuerySnapshot.empty) {
            userDoc = practicesQuerySnapshot.docs[0];
        }
    
        if (userDoc) {
            console.log(userDoc.data());
    
            if (userDoc.exists() && userDoc.data().status === 'Inactive') {
                const auth = getAuth();
                await auth.signOut();
                setStep("login");
                alert('Your account has been deactivated, logging out.. Please contact an administrator.');
            }
        } else {
            console.error("User document not found in either collection!");
        }
      } else {
        console.log("Attempting signup...");
        if (userInfo.password !== userInfo.confirmPassword) {
          throw new Error("Passwords do not match");
        }
        if (!validateForm()) {
          throw new Error("Please fill in all fields");
        }
        const result = await signUp(userInfo.email, userInfo.password, userInfo);
        console.log("Signup result:", result);
        handleSignup(userInfo);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setError(error.message || "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    if (!isLogin) {
      return (
        <>
          <label>
            First Name:
            <input
              type="text"
              name="firstName"
              value={userInfo.firstName}
              onChange={handleUserInfoChange}
              required
            />
          </label>
          <label>
            Last Name:
            <input
              type="text"
              name="lastName"
              value={userInfo.lastName}
              onChange={handleUserInfoChange}
              required
            />
          </label>
          <label>
            Mobile Phone Number:
            <input
              type="tel"
              name="mobile"
              value={userInfo.mobile}
              onChange={handleUserInfoChange}
              required
            />
          </label>
          <label>
            Practice (office):
            <select
              name="practice"
              value={userInfo.practice}
              onChange={handleUserInfoChange}
              required
            >
              <option value="">Select Practice</option>
              {["FWOS", "Park Cities", "Sonoma County", "Hill & Ioppolo", "Ornstein", "Saomsa", "Cosic", "Napa Sonoma", "Facial & Oral Surgery Associates",
                "Petaluma", "Antelope Valley", "Synergy", "Evergreen", "Three Rivers", "Alameda", "Newhall", "WestsideOMS", "Oral Surgery Associates West Ten.",
                "Oral Surgery Associates", "Oregon Oral Surgens", "Elevation Center", "Peninsula Center", "Advanced Oral & Maxillofacial Surgery",
                "Integrative Oral & Facial Surgery", "Shroyer", "Memorial", "Paramount", "Facial Surgery Institute"].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            Role:
            <select
              name="role"
              value={userInfo.role}
              onChange={handleUserInfoChange}
              required
            >
              <option value="">Select Role</option>
              <option value="Standard">Standard</option>
              <option value="Practice Administrator">Practice Administrator</option>
              <option value="System Administrator">System Administrator</option>
            </select>
          </label>
          <label>
            Email Address:
            <input
              type="email"
              name="email"
              value={userInfo.email}
              onChange={handleUserInfoChange}
              required
            />
          </label>
          <label>
            Password:
            <input
              type="password"
              name="password"
              value={userInfo.password}
              onChange={handleUserInfoChange}
              required
            />
          </label>
          <label>
            Confirm Password:
            <input
              type="password"
              name="confirmPassword"
              value={userInfo.confirmPassword}
              onChange={handleUserInfoChange}
              required
            />
          </label>
        </>
      );
    }

    return (
      <>
        <label>
          Email:
          <input
            type="email"
            name="email"
            value={userInfo.email}
            onChange={handleUserInfoChange}
            required
          />
        </label>
        <label>
          Password:
          <input
            type="password"
            name="password"
            value={userInfo.password}
            onChange={handleUserInfoChange}
            required
          />
        </label>
      </>
    );
  };

  const resetUserInfo = () => {
    setUserInfo({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      practice: '',
      role: '',
      password: '',
      confirmPassword: '',
      practiceName: '',
      officeAddress: '',
      officeCity: '',
      officeState: '',
      officeZipCode: '',
      officePhone: ''
    });
  };

  return (
    <div className="login-signup">
      <img src={loginImage} alt="login" className="header-image"/>
      <h1>{isLogin ? 'Login' : 'Sign Up'}</h1>
  
      {error && <p className="error-message">{error}</p>}
      {resetMessage && <p className="success-message">{resetMessage}</p>}
      
      <form onSubmit={handleSubmit}>
        {renderFormFields()}
        <button type="submit" disabled={loading}>
          {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
        </button>
      </form>
      
      {isLogin && (
        <div className="password-reset">
          <button 
            className="reset-password-link" 
            onClick={() => setShowResetPassword(true)}
          >
            Forgot Password?
          </button>
          {showResetPassword && (
            <div className="reset-password-modal">
              <form onSubmit={handleResetPassword}>
                <label>
                  Enter your email:
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </label>
                <div className="reset-password-actions">
                  <button type="submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Reset Password'}
                  </button>
                  <button type="button" onClick={() => setShowResetPassword(false)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
      <p>
        <button onClick={() => {
          setIsLogin(!isLogin);
          setError(null);
          resetUserInfo();
        }}>
          {isLogin ? 'Sign Up' : 'Back to Login'}        
        </button>
      </p>
    </div>
  );  
};

export default Login;
